import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const ZanzibarHoneymoon = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">8 DAYS ZANZIBAR HONEYMOON HOLIDAY</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          8 Days Zanzibar Honeymoon Holiday
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Indulge in a romantic <strong>8 Days Zanzibar Honeymoon Holiday</strong>, where pristine white sand beaches, turquoise waters, and rich cultural experiences await. This luxurious getaway combines relaxing beach days with exciting excursions, including a Spice Tour, sunset cruising, and optional activities like scuba diving and Stone Town exploration, creating unforgettable memories in the tropical paradise of Zanzibar.
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 8 Days Zanzibar Honeymoon Holiday
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrival in Dar Es Salaam to Zanzibar</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Upon arrival at Dar Es Salaam Airport, you will be met and transferred to the ferry terminal for a speedboat crossing to Zanzibar. At Zanzibar Sea Ferry Terminal, a warm meet-and-greet awaits, followed by a transfer to your beachfront accommodation on the East Coast. Check in, refresh, and enjoy a delicious seafood dinner at a local restaurant before settling in for the night.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Apartments & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Spice Tour & Sunset Cruising Supper</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After a delicious breakfast, embark on a Spice Tour to discover why Zanzibar is known as the "Spice Island." Explore plantations abundant with tropical fruits, spices, and herbs like coconut, cardamom, cinnamon, black pepper, lemongrass, nutmeg, and vanilla. Visit Kidichi and Mangapwani to see the Persian Baths built in 1850 by Seyyid Said bin Sultan. Later, transfer to Nungwi for refreshments, followed by a romantic sunset cruise aboard a traditional sailing boat with live music and snacks. Conclude the evening with a barbecue supper at Forodhani Park Gardens before returning to your beach resort.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Apartments & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Days 3-7: White Sands Beach Relaxation</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Enjoy five leisurely days at a pristine white sand beach resort, such as Roja Beach Apartments & Golf or similar. Relax on the beach or partake in optional activities like kayaking, scuba diving, swimming with dolphins, deep-sea fishing, a Stone Town city tour, or a visit to Jozani Forest or Prison Island. These days offer the perfect blend of relaxation and adventure tailored to your preferences.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Roja Beach Apartments & Golf or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 8: Departure from Zanzibar to Dar Es Salaam</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After a final delicious breakfast, enjoy a morning transfer to Stone Town for a last shopping experience at the bazaar and souvenir shops on Mizingani Street, exploring Zanzibar’s charming narrow streets. Afterward, transfer to the Zanzibar Sea Ferry Terminal for your speedboat journey back to Dar Es Salaam. Upon arrival at the Dar Es Salaam Ferry Terminal, you will be met and transferred to the international airport for your flight home.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Transfers to and from Dar Es Salaam and Zanzibar ferry terminals
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Speedboat ferry tickets between Dar Es Salaam and Zanzibar
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodations and meals as stated
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Spice Tour and sunset cruise with supper
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International flights and visa fees
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities (e.g., scuba diving, Jozani Forest tour)
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Alcoholic beverages
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">8 Days Zanzibar Honeymoon Holiday</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 8 Days, 7 Nights
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Dar Es Salaam / Zanzibar
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Airport pickup - Return to airport
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZanzibarHoneymoon;